export const serviceWorkerMessages = {
  registerSync: 'REGISTER_SESSION_SYNC',
  requestSync: 'REQUEST_SYNC_SESSION_QUEUE',
  broadcastSync: 'SYNC_SESSION_QUEUE',
  activated: 'SW_ACTIVATED',
  skipWaiting: 'SKIP_WAITING',
} as const;

export type ServiceWorkerInboundMessage =
  | { type: typeof serviceWorkerMessages.requestSync }
  | { type: typeof serviceWorkerMessages.registerSync }
  | { type: typeof serviceWorkerMessages.skipWaiting };

export type ServiceWorkerOutboundMessage =
  | { type: typeof serviceWorkerMessages.broadcastSync }
  | { type: typeof serviceWorkerMessages.activated };

type NavigatorWithServiceWorker = Navigator & {
  serviceWorker: ServiceWorkerContainer;
};

type SyncManagerLike = {
  register: (tag: string) => Promise<void>;
};

function getNavigator(): NavigatorWithServiceWorker | undefined {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return undefined;
  }

  return navigator as NavigatorWithServiceWorker;
}

function getSyncManager(registration: ServiceWorkerRegistration): SyncManagerLike | undefined {
  const candidate = (registration as ServiceWorkerRegistration & { sync?: SyncManagerLike }).sync;
  if (candidate && typeof candidate.register === 'function') {
    return candidate;
  }
  return undefined;
}

async function postMessageToServiceWorker(message: ServiceWorkerInboundMessage) {
  const navigatorWithSw = getNavigator();
  if (!navigatorWithSw) {
    return;
  }

  try {
    const controller = navigatorWithSw.serviceWorker.controller;
    if (controller) {
      controller.postMessage(message);
      return;
    }

    const registration = await navigatorWithSw.serviceWorker.ready;
    registration.active?.postMessage(message);
  } catch (error) {
    console.warn('Unable to communicate with service worker:', error);
  }
}

export async function requestBackgroundSyncRegistration(): Promise<void> {
  const navigatorWithSw = getNavigator();
  if (!navigatorWithSw) {
    return;
  }

  try {
    const registration = await navigatorWithSw.serviceWorker.ready;
    const syncManager = getSyncManager(registration);

    if (syncManager) {
      await syncManager.register('sync-session-queue');
    }
  } catch (error) {
    console.warn('Background sync registration failed:', error);
  }
}

export async function notifySyncQueueUpdate(): Promise<void> {
  if (!getNavigator()) {
    return;
  }

  await requestBackgroundSyncRegistration();
  await postMessageToServiceWorker({ type: serviceWorkerMessages.requestSync });
}

export async function registerSessionSyncWithServiceWorker(): Promise<void> {
  if (!getNavigator()) {
    return;
  }

  await postMessageToServiceWorker({ type: serviceWorkerMessages.registerSync });
}

export function onServiceWorkerSync(callback: () => void): () => void {
  const navigatorWithSw = getNavigator();
  if (!navigatorWithSw) {
    return () => {};
  }

  const handler = (event: MessageEvent<unknown>) => {
    if (
      event.data &&
      typeof event.data === 'object' &&
      (event.data as Partial<ServiceWorkerOutboundMessage>).type ===
        serviceWorkerMessages.broadcastSync
    ) {
      callback();
    }
  };

  navigatorWithSw.serviceWorker.addEventListener('message', handler);

  return () => {
    navigatorWithSw.serviceWorker.removeEventListener('message', handler);
  };
}

export function onServiceWorkerActivated(callback: () => void): () => void {
  const navigatorWithSw = getNavigator();
  if (!navigatorWithSw) {
    return () => {};
  }

  const handler = (event: MessageEvent<unknown>) => {
    if (
      event.data &&
      typeof event.data === 'object' &&
      (event.data as Partial<ServiceWorkerOutboundMessage>).type === serviceWorkerMessages.activated
    ) {
      callback();
    }
  };

  navigatorWithSw.serviceWorker.addEventListener('message', handler);

  return () => {
    navigatorWithSw.serviceWorker.removeEventListener('message', handler);
  };
}
